import { useEffect, useRef } from "react";

/**
 * Clear the order the trader was composing when the market changes.
 *
 * WHY THIS EXISTS. `<PerpsTradeForm>` is rendered without a `key` and the JSX
 * shape does not change on a market switch (`market.derivative` stays
 * `Perpetual`), so React reuses the component: the react-hook-form instance and
 * every `useState` in `usePerpsTradeFormSetup` survive. Only leverage and margin
 * type were keyed on `instrument_id`, so a limit price typed against one market
 * stayed in the box against the next one -- and so did the size and the TP/SL
 * triggers.
 *
 * Reproduced twice: 40,000 entered on BTC-PERP — 49% BELOW the mark — became
 * 1,524% ABOVE the mark after switching to ETH-PERP, with the Limit tab still
 * selected and the value still displayed. A price is meaningless away from the
 * market it was typed for, and this one is a live order waiting for a click.
 *
 * WHAT IS CLEARED is the caller's choice (`clear`). The perps form clears price,
 * trigger price, size and TP/SL. A TP/SL trigger is a price like any other --
 * a take-profit at 90,000 set on BTC is an instant fill on any market below it.
 * Size goes too: 0.5 BTC and 0.5 ETH are not the same order, and most venues
 * clear it. Leverage and margin type are NOT cleared here; they are per-market
 * settings with their own instrument-keyed effects.
 *
 * NOT ON FIRST RENDER. The first instrument to arrive is not a switch. Clearing
 * then would wipe a price handed in by an orderbook-row click, which arrives
 * through `defaultValues` at mount for the very market being shown.
 */
export function useResetOnMarketSwitch(
  instrumentId: string | undefined,
  clear: () => void
): void {
  // The instrument this form is currently showing. A ref, not state: seeing a new
  // market must not itself cause a render.
  const shownInstrumentId = useRef<string | undefined>(undefined);

  // The callback is re-created every render by callers, so it is deliberately not
  // a dependency -- including it would clear on every render instead of on a
  // switch. The ref below keeps it current.
  const clearRef = useRef(clear);
  clearRef.current = clear;

  useEffect(() => {
    // No market yet: nothing to compare against, and nothing to clear.
    if (!instrumentId) {
      return;
    }

    // First market we have seen. Adopt it silently.
    if (shownInstrumentId.current === undefined) {
      shownInstrumentId.current = instrumentId;
      return;
    }

    if (shownInstrumentId.current === instrumentId) {
      return;
    }

    shownInstrumentId.current = instrumentId;
    clearRef.current();
  }, [instrumentId]);
}
